/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-hooks/rules-of-hooks */
import { Box, Card, ClickAwayListener, Popper, TextField } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import moment from 'moment';
import 'moment/dist/locale/pt-br';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { useBoolean } from 'src/hooks/use-boolean';
import { primary } from 'src/theme/palette';

export interface RhfDatePickerProps {
  /**
   * Nome do campo no yup
   */
  name: string;
  /**
   * Formato de como será exibido no campo.
   * Exemplos:
   * - `'LL'` = **22 de maio de 2025**
   * - `'L'` = **14/05/2025**
   * @default 'LL'
   */
  dateValueFormat: 'L' | 'LL';
  type?: string;
  disabled?: boolean;
  popperZIndex?: number;
}

/**
 * Componente de seleção de data único integrado ao React Hook Form.
 *
 * - Usa o componente `DateCalendar` internamente.
 * - Reage às mudanças de valor conforme o React Hook Form.
 *
 * @param props As propriedades do componente.
 * @param props.name **Nome** do campo controlado pelo RHF.
 * @param props.type **Nome** do campo controlado pelo RHF.
 * @param props.dateValueFormat Forma como será exibido no campo.
 * @param props.disabled
 * @param props.popperZIndex
 * @returns JSX.Element
 */
export default function RhfDatePicker({ name, dateValueFormat = 'LL', disabled, type, popperZIndex }: RhfDatePickerProps) {
  const { control } = useFormContext();

  const usePopper = useBoolean(false);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [selectedDate, setSelectedDate] = useState<any>();
  const [periodLabel, setPeriodLabel] = useState('');

  const handleClosePopper = () => {
    usePopper.onFalse();
    setAnchor(null);
  };

  const handleOpenPopper = (event: React.MouseEvent<HTMLElement>) => {
    usePopper.onTrue();
    setAnchor(event.currentTarget);
  };

  const handleSelectDate = (value: any) => {
    setSelectedDate(value);
  };

  useEffect(() => {
    if (selectedDate) {
      setPeriodLabel(moment(selectedDate).locale('pt-br').format(dateValueFormat));
    } else {
      setPeriodLabel('');
    }
  }, [selectedDate, dateValueFormat]);

  return (
    <div data-testid="rhfDatePicker-render-test">
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => {
          useEffect(() => {
            if (!selectedDate) {
              setSelectedDate(field.value);
            }
          }, [field.value]);

          useEffect(() => {
            field.onChange(selectedDate);
          }, [selectedDate]);

          return (
            <ClickAwayListener onClickAway={handleClosePopper}>
              <Box>
                <TextField
                  fullWidth
                  variant="standard"
                  placeholder="Selecione uma data"
                  value={periodLabel}
                  onClick={(e) => handleOpenPopper(e)}
                  focused={usePopper.value}
                  error={!!error}
                  helperText={error && error?.message}
                  disabled={disabled}
                />
                <Popper placement="bottom-start" open={usePopper.value} anchorEl={anchor} sx={{ zIndex: popperZIndex }}>
                  <Card sx={{ boxShadow: '2px 5px 20px #aaaaaa' }}>
                    <DateCalendar
                      disablePast
                      onChange={handleSelectDate}
                      slotProps={{
                        day: ({ day }) => {
                          const date = dayjs(day);
                          const isSelected = selectedDate && date.isSame(selectedDate, 'day');

                          return {
                            sx: {
                              ...(isSelected && {
                                backgroundColor: `${primary.main} !important`,
                                color: '#fff',
                                borderRadius: '50%',
                                '&:hover': {
                                  backgroundColor: `${primary.main}CC !important`,
                                },
                              }),
                              ':hover': {
                                backgroundColor: `${primary.main}80`,
                              },
                            },
                          };
                        },
                      }}
                    />
                  </Card>
                </Popper>
              </Box>
            </ClickAwayListener>
          );
        }}
      />
    </div>
  );
}
